import type { MessageKey } from '@/shared/i18n/messages';
import type { PlanLimitError } from './types';

/**
 * Recognise the one error the database raises on purpose.
 *
 * `NT001` is Numtable's own SQLSTATE, chosen so a plan ceiling is never
 * confused with a genuine `program_limit_exceeded` from Postgres — offering
 * someone an upgrade because an internal limit was hit would be worse than
 * saying nothing.
 *
 * The code is checked first and the message shape second: a driver that drops
 * the code still leaves the payload, and a message that merely looks like ours
 * without the code is not treated as ours.
 */
const PATTERN = /^plan_limit:([a-z0-9_]+):([a-z0-9_]+):(-?\d+(?:\.\d+)?)$/;

export function parsePlanLimit(error: unknown): PlanLimitError | null {
  if (!error || typeof error !== 'object') return null;

  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code !== 'NT001') return null;
  if (typeof message !== 'string') return null;

  const match = PATTERN.exec(message.trim());
  if (!match) return null;

  return { module: match[1]!, key: match[2]!, limit: Number(match[3]) };
}

/**
 * The sentence for one ceiling.
 *
 * An explicit table rather than a template-literal key, so a limit the
 * interface has no wording for yet falls back to the generic sentence instead
 * of rendering a dotted path at the user. Adding a limit is a row here plus
 * three translations.
 */
const MESSAGE_KEYS: Readonly<Record<string, MessageKey>> = {
  'folders.documents': 'billing.limit.folders.documents',
  'sheets.rows': 'billing.limit.sheets.rows',
  'sheets.cols': 'billing.limit.sheets.cols',
};

export function planLimitMessageKey(error: PlanLimitError): MessageKey {
  return MESSAGE_KEYS[`${error.module}.${error.key}`] ?? 'billing.limit.generic';
}
